import Button from '@/storybook/components/Button/Button';
import Text from '@/storybook/components/Text/Text';
import CrossIcon from '@/storybook/icons/CrossIcon';
import ErrorCircleIcon from '@/storybook/icons/ErrorCircleIcon';
import InfoCircleIcon from '@/storybook/icons/InfoCircleIcon';
import SuccessCircleIcon from '@/storybook/icons/SuccessCircleIcon';
import WarningCircleIcon from '@/storybook/icons/WarningCircleIcon';
import { ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import Styles from './Toast.module.css';
import cx from 'classnames';

export enum ToastAppearance {
  info = 'info',
  success = 'success',
  warning = 'warning',
  error = 'error',
}

export enum ToastSize {
  M = 'M',
  L = 'L',
}

export type Toast = {
  'id'?: string;
  'content': ReactNode;
  'description'?: ReactNode;
  'additionalCta'?: { label: ReactNode; onClick: () => void };
  'appearance'?: keyof typeof ToastAppearance;
  'size'?: keyof typeof ToastSize;
  'autoClose'?: number;
  'data-testid'?: string;
  'className'?: string;
};

export type ToastElementProps = Toast & { id: string; onClose: (id: string) => void };

const ToastIcon: Record<keyof typeof ToastAppearance, () => ReactElement> = {
  info: () => <InfoCircleIcon className={Styles.toastIcon} />,
  success: () => <SuccessCircleIcon className={Styles.toastIcon} />,
  warning: () => <WarningCircleIcon className={Styles.toastIcon} />,
  error: () => <ErrorCircleIcon className={Styles.toastIcon} />,
};

export default function ToastElement({
  id,
  content,
  description,
  additionalCta,
  onClose,
  appearance = 'info',
  size = 'M',
  autoClose = 4000,
  'data-testid': dataTestId = 'toast',
  className,
}: ToastElementProps) {
  const timeoutIdRef = useRef<NodeJS.Timeout>(null);
  const startTimeRef = useRef<number>(0);
  const remainingTimeRef = useRef<number>(autoClose);

  const CurrentToastIcon = useMemo(() => ToastIcon[appearance], [appearance]);

  const handleOnAdditionalCtaClick = useCallback(() => {
    additionalCta?.onClick();
    onClose(id);
  }, [additionalCta, id, onClose]);

  const startTimeout = useCallback(
    (delay: number) => {
      clearTimeout(timeoutIdRef.current);
      startTimeRef.current = Date.now();
      remainingTimeRef.current = delay;
      timeoutIdRef.current = setTimeout(() => onClose(id), delay);
    },
    [id, onClose],
  );

  const pauseTimeout = useCallback(() => {
    if (!timeoutIdRef.current) return;
    clearTimeout(timeoutIdRef.current);
    remainingTimeRef.current -= Date.now() - startTimeRef.current;
  }, []);

  const resumeTimeout = useCallback(() => {
    if (remainingTimeRef.current <= 0) return;
    startTimeout(remainingTimeRef.current);
  }, [startTimeout]);

  useEffect(() => {
    startTimeout(autoClose);
    return () => clearTimeout(timeoutIdRef.current);
  }, [startTimeout, autoClose]);

  return (
    <div
      id={id}
      className={cx(Styles.toastElement, className)}
      data-toast-appearance={appearance}
      data-toast-size={size}
      data-testid={dataTestId}
      onMouseEnter={pauseTimeout}
      onMouseLeave={resumeTimeout}
    >
      <span className={Styles.toastColorBand} />
      <div className={Styles.toastContent}>
        <section className={Styles.toastIconSection}>
          <CurrentToastIcon />
        </section>
        <section className={Styles.toastTextSection}>
          <Text>{content}</Text>
          {description && <Text as="small">{description}</Text>}
        </section>
        <section className={Styles.toastCloseSection}>
          {additionalCta && (
            <Button variant="text" className={Styles.toastAdditionalCta} onClick={handleOnAdditionalCtaClick}>
              {additionalCta.label}
            </Button>
          )}
          <Button
            variant="text"
            iconOnly
            icon={<CrossIcon className={Styles.toastCloseIcon} />}
            onClick={() => onClose(id)}
            data-testid="toast-close-button"
          />
        </section>
      </div>
    </div>
  );
}
